/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { IdleTimerProvider } from 'react-idle-timer';

// auth context
export const AuthContext = React.createContext();

const useAPI = require("components/lib").useAPI;
const Event = require("components/lib").Event;

export function AuthProvider(props) {
  const cache = JSON.parse(localStorage.getItem("user"));
  const [user, setUser] = useState(cache);
  const auth = useAPI(user ? "/api/auth" : null);

  useEffect(() => {
    // update the auth status
    if (!auth.loading && auth.data) {
      auth.data.authenticated ? update(auth.data) : signout();
    }
  }, [auth]);

  useEffect(() => {
    // A page restored from the browser's back/forward cache (bfcache) never
    // re-runs this mount effect or re-renders from a fresh load, so a stale
    // authenticated page (or a stale, still-valid session on the signin
    // page) can resurface untouched when navigating with back/forward.
    // 'pageshow' fires natively on both normal loads and bfcache restores,
    // independent of React's lifecycle, so it catches what mount effects miss.
    function handlePageShow(event) {
      if (window.location.pathname === '/signin') {
        // Reaching the login screen — by any means, including back/forward
        // restoring it from bfcache — should always require fresh credentials.
        if (localStorage.getItem('user')) {
          localStorage.clear();
          sessionStorage.clear();
          setUser(null);
        }
        return;
      }

      if (event.persisted) {
        // Any other page restored from bfcache reflects whatever session
        // state was frozen in memory when the browser cached it. Re-check
        // against the (possibly since-cleared) localStorage instead of
        // trusting that frozen state.
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        if (!stored?.token) window.location.replace('/signin');
      }
    }

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  function signin(res) {
    if (res.data) {
      localStorage.setItem("user", JSON.stringify(res.data));
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + res.data.token;
      Event("signin");

      if (!res.data.plan) return (window.location = "/signup/plan");

      return (window.location = res.data.onboarded ? "/dashboard" : "/welcome");
    }
  }

  async function signout() {
    axios({ method: "delete", url: "/api/auth" });
    localStorage.clear();
    sessionStorage.clear()
    window.localStorage.clear();
    return (window.location = "/signin");
  }

  async function switchAccount(id) {
    const res = await axios({
      method: "post",
      url: "/api/auth/switch",
      data: { account: id },
    });

    if (res.data) signin(res);
  }

  function update(data) {
    if (localStorage.getItem("user")) {
      let user = JSON.parse(localStorage.getItem("user"));
      for (let key in data) {
        if (Array.isArray(data[key])) {
          user[key] = data[key];
        } else if (typeof data[key] === "object") {
          for (let innerKey in data[key]) {
            user[key][innerKey] = data[key][innerKey];
          }
        } else {
          user[key] = data[key];
        }
      }

      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
    }
  }

  return (
    <IdleTimerProvider
      crossTab={true}
      syncTimers={1}
      timeout={60000 * 30}
      onIdle={signout}>

      <AuthContext.Provider
        value={{
          user: user,
          signin: signin,
          signout: signout,
          update: update,
          switchAccount: switchAccount
        }}
        {...props}
      />

    </IdleTimerProvider>
  );
}

// Módulos desactivados globalmente. Para re-habilitar, remover el módulo del array.
export const DISABLED_MODULES = ['vida', 'priori'];

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props) {
  // check user exists
  const user = JSON.parse(localStorage.getItem("user"));

  if (user?.token) {
    if (props.module && DISABLED_MODULES.includes(props.module)) {
      return <Navigate to='/dashboard' />;
    }
    return props.children;
    /*let fakeRol = user.permission;
    let tmpRol = fakeRol.localCompare('vendedor') === 0 ? 'user' : 'master';
    if (permissions[tmpRol][props.permission]) {
      // user has no plan
      if (!user.plan && path !== "/account/profile" && path !== "/signup/plan")
        return <Navigate to='/signup/plan' />;

      // user has no subscription
      if (
        user.subscription !== "active" &&
        user.subscription !== "trialing" &&
        user.permission !== "master"
      ) {
        if (
          !path.includes("/account/billing") &&
          !path.includes("/signup/plan") &&
          !path.includes("/account/upgrade") &&
          !path.includes("/account") &&
          !path.includes("/account/profile")
        ) {
          return <Navigate to='/account/billing' />;
        }
      }

      // user is good
      return props.children;
    }*/
  }

  // user is not authenticated
  return <Navigate to='/signin' />;
}
